/* istanbul ignore file */

import { hideScrollbar, mediaQuery } from '@noths/polaris-client-styles';
import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import type { RuleSet } from 'styled-components';
import { css } from 'styled-components';

import { GALLERY_GRID_MIN_WIDTH } from './constants/layout';

const { spacing } = themeVariables;

/*
  The gallery is one DOM tree in both layouts. A swipe carousel is the base, mobile first, and
  the same slides are laid out as a grid of tiles from GALLERY_GRID_MIN_WIDTH for consumers that
  opt in with `allowGridLayout`. Deciding this in CSS rather than by picking a tree in
  JavaScript is what makes the first paint correct, because the server cannot know the viewport.
*/

/** Styles that apply only while the gallery is showing as a grid of tiles. */
const whenGrid = (styles: RuleSet<object>) =>
  mediaQuery(
    { min: GALLERY_GRID_MIN_WIDTH },
    css`
      [data-allow-grid='true'] & {
        ${styles}
      }
    `,
  );

export const mobileEdgeToEdge = css`
  margin: 0 calc(-1 * var(${spacing.md}));

  ${mediaQuery(
    { min: GALLERY_GRID_MIN_WIDTH },
    css`
      margin: 0;
    `,
  )}
`;

export const gallery = css`
  position: relative;
`;

export const mediaTrack = css`
  position: relative;
  display: flex;
  overflow-x: auto;
  scroll-behavior: auto;
  scroll-snap-type: x mandatory;

  ${hideScrollbar}

  &[data-media-focus='true'] {
    box-shadow: 0 0 0 3px var(--neutral-white-base), 0 0 0 5px var(--brand-purple-base);
  }

  ${whenGrid(css`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(${spacing.xs});
    /* Not scrollable, so nothing is hidden off to the side and the scroll/state sync stands
       down (see useProductMediaCarousel). */
    overflow: visible;
    scroll-snap-type: none;

    /* Tiles take the browser's own focus ring; the carousel's ring around the whole track
       would read as the wrong element being focused. */
    &[data-media-focus='true'] {
      box-shadow: none;
    }
  `)}
`;

export const carouselSlide = css`
  scroll-snap-align: center;
  scroll-snap-stop: always;
  min-width: 100%;
  height: 0;
  padding-bottom: 100%;
  position: relative;
  display: flex;
  align-items: center;
  border: none;
  background-color: transparent;

  ${whenGrid(css`
    /* Sized by its grid column now, not by the viewport. Height stays 0 + 100% padding, so a
       tile is square whatever column it lands in. */
    min-width: 0;
    scroll-snap-align: none;
    cursor: pointer;

    /* The first item is the hero tile across both columns, the next two sit beneath it, and
       the rest are reachable through the "View all photos" pill. */
    &:first-child {
      grid-column: 1 / -1;
    }

    &:nth-child(n + 4) {
      display: none;
    }
  `)}

  ${mediaQuery(
    { min: GALLERY_GRID_MIN_WIDTH },
    css`
      /* Two items: the hero tile and the pill alone, rather than one lone half-width
         thumbnail beside a gap. */
      [data-allow-grid='true'][data-media-count='2'] & {
        &:nth-child(n + 2) {
          display: none;
        }
      }
    `,
  )}
`;

export const mainMedia = css`
  width: 100%;
  height: 100%;
  object-fit: contain;

  ${whenGrid(css`
    /* Tiles are read as a composed set, so fill each one rather than letterboxing it. */
    object-fit: cover;
  `)}
`;

export const favouriteButtonContainer = css`
  position: absolute;
  right: 12px;
  top: 12px;
  display: flex;
`;

export const imageNumberLabelContainer = css`
  position: absolute;
  bottom: 12px;
  left: 12px;

  ${whenGrid(css`
    /* Every tile is on screen at once, so there is no position to report. */
    display: none;
  `)}
`;

export const carouselDotsContainer = css`
  ${whenGrid(css`
    display: none;
  `)}
`;

export const mediaCountPillContainer = css`
  /* The counter and dots are the carousel's affordance; the pill is the grid's. */
  display: none;

  ${whenGrid(css`
    display: block;
  `)}
`;
